import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortController } from 'src/types/interfaces/sort-controller';
import { useTranslation } from 'react-i18next';
import { TableColumn } from 'src/types/interfaces/table-column';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { SortOrder } from 'src/types/enum/sort-order';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import GeneralStatisticsTableRow from '../molecules/general-statistics-table-row';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import TypeSpecificStatisticsTableRow from '../molecules/type-specific-statistics-table-row';
import { BookingStatisticFiltered } from '../../types/booking-statistic-filtered';
import { ContentType } from '../../types/content-type';
import { StatisticsVariant } from '../../types/statistic-variant';
import { useMemo } from 'react';

interface Props {
  data: BookingStatisticFiltered[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  contentType: ContentType;
  variant: StatisticsVariant;
}

export default function StatisticsTable({
  data,
  isLoading,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  contentType,
  variant,
}: Props) {
  const { t } = useTranslation(['statistics', 'common']);

  const generalTableColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'date',
        label: t('statistics:common.columns.general.date'),
        isSortable: true,
      },
      {
        id: 'room',
        label: t('statistics:common.columns.general.room'),
        isSortable: true,
      },
      {
        id: 'desk',
        label: t('statistics:common.columns.general.desk'),
        isSortable: true,
      },
    ],
    [t],
  );

  const detailedTableColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'date',
        label: t('statistics:common.columns.type.date'),
        isSortable: true,
      },
      {
        id: 'bookings',
        label: t('statistics:common.columns.type.bookings'),
        isSortable: true,
      },
    ],
    [t],
  );

  const columns: TableColumn[] =
    contentType === 'GENERAL' ? generalTableColumns : detailedTableColumns;

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={sortController.sortBy === column.id}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(item =>
              contentType === 'GENERAL' ? (
                <GeneralStatisticsTableRow
                  key={item.date}
                  data={item}
                  variant={variant}
                />
              ) : (
                <TypeSpecificStatisticsTableRow
                  key={item.date}
                  date={item.date}
                  bookingsCount={
                    contentType === 'DESK'
                      ? item._filteredDESK
                      : item._filteredROOM
                  }
                  variant={variant}
                />
              ),
            )}
          </TableBody>
        </Table>
      </Scrollbar>

      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
}
